import React, { useEffect, useState } from 'react';
import { Typography, Box, Button, Grid, Dialog, DialogContent, DialogActions, Toolbar } from '@mui/material';
import { Form, Field } from 'react-final-form';
import TextField from '../../components/text_field';
import {
  getPayPalConnectionStatus,
  initiatePayPalOnboarding,
  savePayPalSettings,
  disconnectPayPal,
  ConnectionStatus,
  ConnectionStatusType
} from './paypal_service';
import { pageContentGap } from '../../theme';

interface PayPalDashboardProps {
  setTitle?: (title: string) => void;
  isRetailer?: boolean;
}

const styles = {
  root: {
    backgroundColor: '#e9edf1',
    minHeight: 'calc(100vh - 64px)',
    padding: '32px 35px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 700,
    fontFamily: 'Poppins',
    color: '#012237',
  },
  pageSubtitle: {
    textAlign: 'left' as const,
    fontSize: '14px',
    fontFamily: 'Poppins',
    color: 'rgba(0, 0, 0, 0.7)',
    marginBottom: '24px',
    maxWidth: '100%',
  },
  paypalLogo: {
    height: '40px',
  },
  contentContainer: {
    maxWidth: '100%',
    marginLeft: '0',
    marginRight: '0',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '16px',
  },
  connectionCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
  },
  resourcesCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    border: '1px solid #e0e0e0',
  },
  aboutCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: '#fff8f8',
    borderRadius: '8px',
    padding: '24px',
    border: '1px solid #ffcdd2',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    fontFamily: 'Poppins',
    color: '#012237',
    marginBottom: '12px',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  statusLabel: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    fontWeight: 500,
    color: '#012237',
    marginRight: '8px',
  },
  statusConnected: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: '#4caf50',
  },
  statusNotConnected: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: '#d32f2f',
  },
  description: {
    textAlign: 'left' as const,
    fontSize: '14px',
    fontFamily: 'Poppins',
    color: 'rgba(0, 0, 0, 0.7)',
    marginBottom: '16px',
    lineHeight: 1.6,
  },
  connectButton: {
    backgroundColor: '#4D57F8',
    color: '#ffffff',
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontWeight: 600,
    padding: '10px 24px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#3D47E8',
    },
  },
  disconnectButton: {
    backgroundColor: '#d32f2f',
    color: '#ffffff',
    textTransform: 'none' as const,
    fontFamily: 'Poppins',
    fontWeight: 600,
    padding: '10px 24px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#b71c1c',
    },
  },
  helperText: {
    fontSize: '13px',
    fontFamily: 'Poppins',
    color: 'rgba(0, 0, 0, 0.6)',
    marginTop: '12px',
  },
  resourcesList: {
    textAlign: 'left' as const,
    listStyle: 'disc' as const,
    paddingLeft: '20px',
    margin: 0,
  },
  resourceItem: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    color: '#012237',
    marginBottom: '8px',
  },
  resourceLink: {
    color: '#012237',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  dialogContent: {
    minWidth: '500px',
  },
  resultMessage: {
    whiteSpace: 'pre-wrap' as const,
    textAlign: 'left' as const,
  },
  settingsCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    marginTop: '16px',
  },
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '12px 16px',
    width: '100%',
    textAlign: 'left' as const,
  },
  settingLabel: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    fontWeight: 500,
    color: '#012237',
  },
  settingValue: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    color: 'rgba(0, 0, 0, 0.7)',
    wordBreak: 'break-word' as const,
  },
};

const PayPalDashboard: React.FC<PayPalDashboardProps> = (props) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusType>(ConnectionStatus.NOT_CONNECTED);
  const [loading, setLoading] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [initialSettings, setInitialSettings] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (props.setTitle) {
      props.setTitle('PayPal Agentic Dashboard');
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.setTitle]);

  const loadData = async () => {
    try {
      const statusResult = await getPayPalConnectionStatus();
      setConnectionStatus(statusResult.status);
      setInitialSettings(statusResult.settings || {});
    } catch (error) {
      console.error('Error loading PayPal data:', error);
    }
  };

  const handleOpenConnectDialog = () => {
    setShowConnectDialog(true);
  };

  const handleCloseConnectDialog = () => {
    setShowConnectDialog(false);
  };

  const handleOnboardPayPal = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      // 1. Save settings first
      await savePayPalSettings(values);

      // 2. Execute the onboarding action
      await initiatePayPalOnboarding(values);

      // 3. Redirect to OAuth flow (Connect action)
      window.location.href = '/oauth/PayPal';
    } catch (error: any) {
      console.error('Error initiating PayPal onboarding:', error);
      setResultMessage('Error: ' + (error.response?.data || error.message));
      setShowResultDialog(true);
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await disconnectPayPal();
      await loadData();
    } catch (error) {
      console.error('Error disconnecting PayPal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (values: Record<string, unknown>) => {
    try {
      // 1. Save settings
      await savePayPalSettings(values);

      // 2. Re-run the onboarding action
      await initiatePayPalOnboarding(values);

      // 3. Reload data to reflect changes
      await loadData();
    } catch (error: any) {
      console.error('Error saving PayPal settings:', error);
      setResultMessage('Error: ' + (error.response?.data || error.message));
      setShowResultDialog(true);
    }
  };

  const isConnected = connectionStatus === ConnectionStatus.CONNECTED;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.contentContainer}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box>
            <Typography sx={styles.pageTitle}>
              PayPal Agentic Dashboard
            </Typography>
          </Box>
          <img
            src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-200px.png"
            alt="PayPal"
            style={styles.paypalLogo}
          />
        </Box>

        {/* Subtitle */}
        <Typography sx={styles.pageSubtitle}>
          Manage your connection to PayPal Agentic Commerce. Activate your PayPal account to enable
          intelligent commerce experiences through Logicbroker.
        </Typography>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column - Connection Section */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={styles.connectionCard}>
              <Typography sx={styles.sectionTitle}>
                PayPal Connection
              </Typography>
              <Box sx={styles.statusRow}>
                <Typography sx={styles.statusLabel}>Status:</Typography>
                <Typography sx={isConnected ? styles.statusConnected : styles.statusNotConnected}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </Typography>
              </Box>
              {isConnected && (initialSettings.PayPalMerchantId || initialSettings.PayPalBaseUrl) && (
                <Box sx={{ ...styles.settingsGrid, marginBottom: '16px' }}>
                  {initialSettings.PayPalMerchantId && (
                    <>
                      <Typography sx={styles.settingLabel}>Merchant ID:</Typography>
                      <Typography sx={styles.settingValue}>{initialSettings.PayPalMerchantId}</Typography>
                    </>
                  )}
                  {initialSettings.PayPalBaseUrl && (
                    <>
                      <Typography sx={styles.settingLabel}>Environment:</Typography>
                      <Typography sx={styles.settingValue}>
                        {initialSettings.PayPalBaseUrl.toLowerCase().includes('sandbox') ? 'Sandbox' : 'Production'}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              <Typography sx={styles.description}>
                {isConnected
                  ? 'Your PayPal account is connected to Logicbroker. Agentic Commerce features are now enabled.'
                  : 'Your PayPal account is not yet connected to Logicbroker. You must complete the PayPal onboarding flow to activate Agentic Commerce.'}
              </Typography>
              {isConnected ? (
                <Box style={{ display: 'flex', gap: pageContentGap }}>
                  <Button
                    variant="contained"
                    sx={styles.connectButton}
                    onClick={handleOpenConnectDialog}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Reconnect'}
                  </Button>
                  <Button
                    variant="contained"
                    sx={styles.disconnectButton}
                    onClick={handleDisconnect}
                    disabled={loading}
                  >
                    Disconnect
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  sx={styles.connectButton}
                  onClick={handleOpenConnectDialog}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Connect to PayPal'}
                </Button>
              )}
              <Typography sx={styles.helperText}>
                {isConnected
                  ? 'Reconnect to update your settings or disconnect to remove PayPal from Logicbroker.'
                  : 'This will open a dialog to configure your PayPal connection.'}
              </Typography>
            </Box>

            {/* Settings Section - Only shown when connected */}
            {isConnected && Object.keys(initialSettings).length > 0 && (
              <Box sx={styles.settingsCard}>
                <Typography sx={styles.sectionTitle}>
                  Connection Settings
                </Typography>
                {/* Editable fields */}
                <Form
                  onSubmit={handleSaveSettings}
                  initialValues={initialSettings}
                  render={({ handleSubmit, pristine, submitting }) => (
                    <form onSubmit={handleSubmit}>
                      <Field
                        component={TextField as any}
                        name="PayPalTrackingId"
                        label="Tracking ID"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalBaseUrl"
                        label="Base URL"
                        placeholder="https://api-m.sandbox.paypal.com"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalMerchantCountry"
                        label="Merchant Country"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalMerchantCurrency"
                        label="Merchant Currency"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalMerchantIconUrl"
                        label="Merchant Icon URL"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalMerchantStoreName"
                        label="Merchant Store Name"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalMerchantStoreUrl"
                        label="Merchant Store URL"
                        fullWidth
                      />
                      <Field
                        component={TextField as any}
                        name="PayPalMerchantShippingCountries"
                        label="Merchant Shipping Countries"
                        fullWidth
                      />
                      <Box style={{ marginTop: '16px', textAlign: 'left' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={pristine || submitting}
                        >
                          {submitting ? 'Saving...' : 'Save'}
                        </Button>
                      </Box>
                    </form>
                  )}
                />
              </Box>
            )}

            {/* About Section */}
            <Box sx={{ ...styles.aboutCard, marginTop: '16px' }}>
              <Typography sx={styles.sectionTitle}>
                About PayPal Agentic Commerce
              </Typography>
              <Typography sx={{ ...styles.description, marginBottom: 0 }}>
                Connecting your PayPal Business account allows Logicbroker to process Agentic Commerce
                transactions on your behalf. This includes creating carts, validating orders, and capturing
                payments through PayPal. Your PayPal onboarding must be completed before these features can
                be enabled.
              </Typography>
            </Box>
          </Grid>

          {/* Right Column - Resources */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.resourcesCard}>
              <Typography sx={styles.sectionTitle}>
                Resources
              </Typography>
              <ul style={styles.resourcesList}>
                <li style={styles.resourceItem}>
                  <span style={styles.resourceLink}>
                    PayPal Agentic Commerce Overview
                  </span>
                </li>
                <li style={styles.resourceItem}>
                  <span style={styles.resourceLink}>
                    Logicbroker Agentic Commerce Documentation
                  </span>
                </li>
                <li style={styles.resourceItem}>
                  <span style={styles.resourceLink}>
                    <a href="https://help.logicbroker.com" target="_blank" rel="noopener noreferrer">
                      Contact Support
                    </a>
                  </span>
                </li>
              </ul>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Connect to PayPal Dialog */}
      <Dialog open={showConnectDialog} fullWidth maxWidth="sm" onClose={handleCloseConnectDialog}>
        <Toolbar className='lbtoolbar'>Connect to PayPal</Toolbar>
        <Form
          onSubmit={handleOnboardPayPal}
          initialValues={initialSettings}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={styles.dialogContent}>
                <Field
                  component={TextField as any}
                  name="PayPalTrackingId"
                  label="Tracking ID"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalBaseUrl"
                  label="Base URL"
                  placeholder="https://api-m.sandbox.paypal.com"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalMerchantStoreName"
                  label="Merchant Store Name"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalMerchantStoreUrl"
                  label="Merchant Store URL"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalMerchantCountry"
                  label="Merchant Country"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalMerchantCurrency"
                  label="Merchant Currency"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalMerchantIconUrl"
                  label="Merchant Icon URL"
                  fullWidth
                />
                <Field
                  component={TextField as any}
                  name="PayPalMerchantShippingCountries"
                  label="Merchant Shipping Countries"
                  fullWidth
                />
              </DialogContent>
              <DialogActions>
                <Button variant="text" onClick={handleCloseConnectDialog}>Cancel</Button>
                <Button
                  variant="text"
                  color="primary"
                  type="submit"
                  disabled={submitting || loading}
                >
                  {loading ? 'Processing...' : 'Onboard PayPal Agentic'}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={showResultDialog} fullWidth maxWidth="sm" onClose={() => setShowResultDialog(false)}>
        <Toolbar className='lbtoolbar'>Results</Toolbar>
        <DialogContent>
          <Typography sx={styles.resultMessage}>{resultMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowResultDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayPalDashboard;
